import { useState, type FormEvent } from "react";
import Reveal from "@/components/feature/Reveal";

const SUBMIT_URL = "https://readdy.ai/api/form/dajp1tnh3l9b18n2bi40";

type Status = "idle" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);
  const [showDriveGuide, setShowDriveGuide] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value);
    });

    setSending(true);
    setStatus("idle");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "送信に失敗しました。時間をおいて再度お試しください。");
      }
    } catch {
      setStatus("error");
      setFormError("送信に失敗しました。通信環境をご確認のうえ再度お試しください。");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 font-body text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-400";
  const labelClass = "mb-2 block font-label text-xs font-bold text-foreground-700";

  return (
    <section id="contact" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-3xl px-4 md:px-6">
        <Reveal className="mb-10 text-center md:mb-14">
          <p className="font-label text-[36px] font-bold tracking-[0.15em] text-foreground-700 md:text-[40px]">CONTACT</p>
          <h2 className="mt-3 font-heading text-3xl font-black leading-snug text-foreground-950 md:text-5xl">
            資料請求・お問い合わせ
          </h2>
          <p className="mt-4 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            導入のご相談やお見積りは無料です。以下フォームよりお気軽にお問い合わせください。
          </p>
        </Reveal>

        <Reveal delay={100}>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/10 text-primary-600">
                  <i className="ri-check-line text-3xl" />
                </span>
                <p className="mt-5 font-heading text-lg font-black text-foreground-950 md:text-xl">
                  送信が完了しました
                </p>
                <p className="mt-3 max-w-md font-body text-sm leading-relaxed text-foreground-600">
                  お問い合わせありがとうございます。担当者より2営業日以内にご連絡いたします。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 rounded-md border border-primary-500/50 px-6 py-2.5 font-label text-sm font-bold text-primary-600 transition-colors hover:bg-primary-500/10"
                >
                  続けて送信する
                </button>
              </div>
            ) : (
              <form data-readdy-form id="contact-form" onSubmit={handleSubmit} noValidate>
                <input
                  type="text"
                  name="website_alt"
                  className="form-field-alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className={labelClass} htmlFor="company">
                      会社名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      placeholder="例）株式会社ナンバ"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="name">
                      お名前 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="例）山田 太郎"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="email">
                      メールアドレス <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="例）taro@example.com"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="phone">
                      電話番号
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="例）03-1234-5678"
                      className={inputClass}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass} htmlFor="employees">
                      従業員規模
                    </label>
                    <select id="employees" name="employees" defaultValue="" className={inputClass}>
                      <option value="" disabled>
                        選択してください
                      </option>
                      <option value="1-50">1〜50名</option>
                      <option value="51-300">51〜300名</option>
                      <option value="301-1000">301〜1,000名</option>
                      <option value="1001-">1,001名以上</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass} htmlFor="video_url">
                      HP
                    </label>
                    <input
                      id="video_url"
                      name="video_url"
                      type="url"
                      placeholder="例）https://"
                      className={inputClass}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass} htmlFor="drive_url">
                      ファイル共有リンク（Googleドライブ等）
                    </label>
                    <input
                      id="drive_url"
                      name="drive_url"
                      type="url"
                      placeholder="例）https://drive.google.com/..."
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={() => setShowDriveGuide((prev) => !prev)}
                      aria-expanded={showDriveGuide}
                      className="mt-2 inline-flex items-center gap-1 font-label text-xs font-bold text-secondary-700 transition-colors hover:text-secondary-900"
                    >
                      <i
                        className={`text-base transition-transform ${showDriveGuide ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
                      />
                      共有方法はこちら
                    </button>

                    {showDriveGuide && (
                      <div className="mt-3 space-y-3 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3">
                        <div>
                          <p className="mb-1.5 font-label text-xs font-bold text-secondary-900">
                            Googleドライブへのアップロード手順
                          </p>
                          <ol className="list-decimal space-y-1 pl-4 text-xs leading-relaxed text-foreground-700 marker:text-secondary-500">
                            <li>Googleドライブを開き、ファイルを保存したいフォルダを開きます。</li>
                            <li>右上の「＋ 新規」→「ファイルのアップロード」を選び、送りたいファイルを選択します。</li>
                            <li>アップロードが完了するまでお待ちください（数GBの大きいファイルは時間がかかります）。</li>
                            <li>アップロードしたファイルの共有リンクをコピーし、上の入力欄に貼り付けます。</li>
                          </ol>
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <figure className="overflow-hidden rounded-md border border-secondary-200 bg-background-50">
                            <div className="h-32 w-full overflow-hidden sm:h-28">
                              <img
                                src="https://readdy.ai/api/search-image?query=Clean%20flat%20UI%20illustration%20of%20a%20cloud%20file%20storage%20web%20application%2C%20main%20content%20area%20with%20a%20folder%20list%2C%20a%20prominent%20colorful%20plus%20icon%20button%20labeled%20New%20in%20the%20top%20right%20corner%20highlighted%20with%20a%20red%20circle%20annotation%2C%20minimal%20modern%20design%2C%20light%20gray%20and%20white%20background%2C%20soft%20muted%20colors&width=800&height=450&seq=contact-drive-guide-new-01&orientation=landscape"
                                alt="Googleドライブの「＋ 新規」ボタンの位置を示した図解"
                                title="Googleドライブ ファイルのアップロード 図解"
                                className="h-full w-full object-cover object-top"
                              />
                            </div>
                            <figcaption className="flex items-start gap-1.5 px-3 py-2 text-[11px] leading-relaxed text-foreground-600">
                              <i className="ri-add-circle-line mt-0.5 text-sm text-secondary-500" />
                              <span>右上の「＋ 新規」から「ファイルのアップロード」を選びます。</span>
                            </figcaption>
                          </figure>

                          <figure className="overflow-hidden rounded-md border border-secondary-200 bg-background-50">
                            <div className="h-32 w-full overflow-hidden sm:h-28">
                              <img
                                src="https://readdy.ai/api/search-image?query=Clean%20flat%20UI%20illustration%20of%20a%20file%20sharing%20settings%20dialog%20window%2C%20a%20copy%20link%20button%20highlighted%20with%20a%20red%20circle%20annotation%2C%20access%20permission%20dropdown%20showing%20anyone%20with%20the%20link%2C%20minimal%20modern%20design%2C%20white%20background%2C%20soft%20muted%20colors&width=800&height=450&seq=contact-drive-guide-share-01&orientation=landscape"
                                alt="共有リンクをコピーするボタンの位置を示した図解"
                                title="Googleドライブ 共有リンク コピー 図解"
                                className="h-full w-full object-cover object-top"
                              />
                            </div>
                            <figcaption className="flex items-start gap-1.5 px-3 py-2 text-[11px] leading-relaxed text-foreground-600">
                              <i className="ri-link-m mt-0.5 text-sm text-secondary-500" />
                              <span>共有設定で「リンクをコピー」し、上の入力欄に貼り付けます。</span>
                            </figcaption>
                          </figure>
                        </div>

                        <p className="flex items-start gap-1.5 rounded-md bg-background-50 px-3 py-2 text-xs leading-relaxed text-foreground-700">
                          <i className="ri-information-line mt-0.5 text-base text-secondary-500" />
                          <span>
                            数GBなど容量の大きいファイルは、メールに添付して送ることができません。Googleドライブに保存し、共有リンクの形でお送りください。
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass} htmlFor="message">
                      お問い合わせ内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      placeholder="ご相談内容をご記入ください（500文字以内）"
                      className={`${inputClass} resize-none`}
                    />
                  </div>
                </div>

                {status === "error" && (
                  <p className="mt-5 flex items-start gap-2 rounded-md bg-primary-500/10 px-4 py-3 font-body text-sm text-primary-700">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{formError}</span>
                  </p>
                )}

                <div className="mt-7 flex flex-col items-center gap-4">
                  <button
                    type="submit"
                    disabled={sending}
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-8 py-4 font-label text-sm font-bold text-white transition-colors hover:bg-primary-600 disabled:opacity-60 sm:w-auto sm:min-w-[240px] whitespace-nowrap"
                  >
                    {sending ? "送信中..." : "この内容で送信する"}
                    {!sending && <i className="ri-send-plane-line" />}
                  </button>
                  <p className="font-body text-xs text-foreground-500">
                    送信いただいた内容は、お問い合わせ対応の目的にのみ利用します。
                  </p>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}